import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle, Star } from "lucide-react";
import { Stand3D } from "./Stand3D";
import { CONTACT } from "./data";
import { scrollToId } from "./Reveal";

const LINES = [
  [{ t: "Fiecare client" }],
  [{ t: "mulțumit," }, { t: " o stea", em: true }],
  [{ t: "în plus pe Google." }],
];
const ease = [0.16, 1, 0.3, 1];

const Chip = ({ className, delay, children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.8, delay, ease }}
    className={`absolute z-10 rounded-2xl bg-white/80 backdrop-blur-xl border border-white shadow-[0_20px_50px_-25px_rgba(11,17,32,0.45)] px-4 py-3 ${className}`}
  >
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const standY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain relative min-h-[100svh] overflow-hidden pt-28 sm:pt-32 pb-16">
      <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#A8DCF2]/50 blur-[120px] pointer-events-none" />
      <motion.p style={{ x: wordX }} aria-hidden className="pointer-events-none absolute bottom-0 left-0 whitespace-nowrap font-display italic font-black text-outline text-[28vw] leading-[0.8] select-none">
        EasyREW. EasyREW.
      </motion.p>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="font-mono text-xs uppercase tracking-[0.25em] text-[#1F6F99] mb-6">
            Carduri & standuri NFC pentru recenzii Google
          </motion.p>
          <h1 data-testid="hero-headline" className="font-display font-semibold text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.4rem] tracking-[-0.02em]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.35 + i * 0.13, ease }}>
                  {line.map((p, j) => (
                    <span key={j} className={p.em ? "italic font-black text-[#5FB4E2]" : ""}>{p.t}</span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p data-testid="hero-subheadline" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }} className="mt-8 max-w-lg text-base sm:text-lg text-[#334155] leading-relaxed">
            Un stand mic de acrilic pe tejghea. Clientul apropie telefonul și pagina ta de recenzii Google se deschide instant. Fără aplicații, fără abonament.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }} className="mt-10 flex flex-wrap gap-3">
            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp" className="group inline-flex items-center gap-2.5 rounded-full bg-[#0B1120] text-white px-6 py-3.5 text-sm sm:text-base hover:bg-[#1F6F99] transition-colors">
              <MessageCircle className="w-4 h-4" /> Cere o ofertă pe WhatsApp
            </a>
            <button onClick={() => scrollToId("cum-functioneaza")} data-testid="hero-cta-explore" className="group inline-flex items-center gap-2 rounded-full border border-[#0B1120]/20 px-6 py-3.5 text-sm sm:text-base hover:border-[#0B1120] transition-colors">
              Vezi cum funcționează <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: standY }} className="lg:col-span-5 relative">
          <motion.div initial={{ opacity: 0, y: 60, rotate: -4 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 1.4, delay: 0.5, ease }}>
            <Stand3D />
          </motion.div>
          <Chip className="left-0 sm:-left-4 top-8" delay={1.4}>
            <div className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" />)}</div>
            <p className="mt-1 text-xs text-[#334155]">Recenzie nouă pe Google</p>
          </Chip>
          <Chip className="right-0 sm:-right-2 bottom-16" delay={1.6}>
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#1F6F99]">Timp</p>
            <p className="font-display italic font-black text-2xl">~5 sec</p>
          </Chip>
        </motion.div>
      </div>
    </section>
  );
};
